<template>
  <div class="aigame-container">
    <!-- 遊戲開始前:學生資訊輸入 -->
    <div v-if="beforeGame" class="before-game-container">
      <div v-if="isFromActId" class="actid-mode-wrapper">
        <div class="actid-mode-modal">
          <!-- 左側:遊戲資訊和播放按鈕 -->
          <div class="game-info-left">
            <div class="game-title">{{ gameConfig.topic || '自定義遊戲' }}</div>
            <div class="game-info">
              <div class="game-type">{{ $t('gameActManager.aiGame') }}</div>
            </div>
            <!-- 按鈕區 -->
            <div class="left-buttons">
              <button class="action-button play-button play-in-left" @click="startGame">
                <svg-icon icon-class="play" class="play-icon" />
              </button>
            </div>
          </div>

          <!-- 右側:學生資訊輸入 -->
          <div class="student-info-right">
            <h4 class="student-info-title">{{ $t('gameActManager.enterStudentInfo') }}</h4>
            <div class="form-group">
              <input
                v-model="studentInfo.seatNumber"
                type="text"
                :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                maxlength="3"
                class="student-input"
              />
              <span v-if="studentInfoErrors.seatNumber" class="error-text">{{ studentInfoErrors.seatNumber }}</span>
            </div>
            <div class="form-group">
              <input
                v-model="studentInfo.name"
                type="text"
                :placeholder="$t('gameActManager.studentNamePlaceholder')"
                maxlength="20"
                class="student-input"
              />
              <span v-if="studentInfoErrors.name" class="error-text">{{ studentInfoErrors.name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 預覽模式:直接開始 -->
      <div v-else class="preview-mode-wrapper">
        <div class="preview-modal">
          <h2>{{ $t('gameActManager.previewMode') }}</h2>
          <p>{{ gameConfig.topic || '自定義遊戲預覽' }}</p>
          <button class="start-button" @click="startGame">
            {{ $t('gameActManager.confirmStudentInfo') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 遊戲進行中:載入 iframe -->
    <div v-else class="game-iframe-container">
      <iframe
        ref="gameIframe"
        :srcdoc="gameHtml"
        sandbox="allow-scripts"
        class="game-iframe"
        @load="onIframeLoad"
      ></iframe>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '../components/SvgIcon.vue';
import gameActivityAPI from '@/api/gameActivityAPI';
import api from '@/api';
import jwt_decode from 'jwt-decode';

const { t } = useI18n();
const route = useRoute();

// 狀態
const beforeGame = ref(true);
const gameHtml = ref('');
const gameConfig = ref({
  topic: '',
  customCode: ''
});

// 學生資訊
const studentInfo = ref({
  name: '',
  seatNumber: ''
});
const studentInfoErrors = ref({
  name: '',
  seatNumber: ''
});

// 判斷是否從 actId 進入
const isFromActId = computed(() => {
  return !!route.query.actId;
});

// 判斷是否為預覽模式
const isPreviewMode = computed(() => {
  // 從 localStorage 讀取預覽資料
  const previewData = localStorage.getItem('game_preview_data');
  return !!previewData && !isFromActId.value;
});

// Refs
const gameIframe = ref(null);

// 驗證學生資訊
const validateStudentInfo = () => {
  studentInfoErrors.value = { name: '', seatNumber: '' };
  let isValid = true;

  // 驗證姓名
  if (!studentInfo.value.name.trim()) {
    studentInfoErrors.value.name = t('gameActManager.studentNameRequired');
    isValid = false;
  } else if (studentInfo.value.name.length > 20) {
    studentInfoErrors.value.name = t('gameActManager.studentNameTooLong');
    isValid = false;
  }

  // 驗證座號 (選填,但如果填了要驗證格式)
  if (studentInfo.value.seatNumber.trim()) {
    const seatNum = parseInt(studentInfo.value.seatNumber);
    if (isNaN(seatNum) || seatNum < 1 || seatNum > 999) {
      studentInfoErrors.value.seatNumber = t('gameActManager.seatNumberInvalid');
      isValid = false;
    }
  }

  return isValid;
};

// 開始遊戲
const startGame = () => {
  // 如果是正式活動,需要驗證學生資訊
  if (isFromActId.value) {
    if (!validateStudentInfo()) {
      return;
    }
  }

  // 隱藏開始畫面,顯示遊戲
  beforeGame.value = false;
};

// iframe 載入完成
const onIframeLoad = () => {
  console.log('[AI Game] iframe 載入完成');
};

// 從 actId 載入遊戲活動
const loadActivityFromActId = async (actId) => {
  try {
    console.log('[AI Game] 載入活動:', actId);

    const response = await gameActivityAPI.getGameActivity({ id: actId });
    console.log('[AI Game] API 回應:', response);

    if (response && response.status === 200 && response.data) {
      const activityData = response.data;
      console.log('[AI Game] activityData:', activityData);

      // 檢查活動是否開放進入
      if (!activityData.enableEntry) {
        console.warn('[AI Game] 活動未開放進入');
        gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; flex-direction: column; color: #f56c6c;"><div style="font-size: 24px; margin-bottom: 16px;">⚠️</div><div>' + t('gameActManager.activityNotOpen') + '</div></div>';
        beforeGame.value = false;
        return;
      }

      // 解析 gameContentSettings (JSON 字串)
      let gameParameters = null;
      if (activityData.gameContentSettings) {
        try {
          gameParameters = JSON.parse(activityData.gameContentSettings);
          console.log('[AI Game] gameParameters:', gameParameters);
        } catch (e) {
          console.error('[AI Game] 解析 gameContentSettings 失敗:', e);
        }
      }

      // 設定遊戲配置
      gameConfig.value = {
        topic: gameParameters?.topic || activityData.activityName || '自定義遊戲',
        customCode: gameParameters?.customCode || ''
      };

      // 設定 gameHtml
      gameHtml.value = gameConfig.value.customCode;

      console.log('[AI Game] 活動載入成功:', gameConfig.value.topic);
      console.log('[AI Game] customCode 長度:', gameConfig.value.customCode.length);

      if (!gameConfig.value.customCode) {
        console.warn('[AI Game] customCode 是空的！');
        gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; color: #f56c6c;">遊戲代碼為空</div>';
      }
    } else {
      console.error('[AI Game] 活動資料格式錯誤，response:', response);
      gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; color: #f56c6c;">載入遊戲失敗：資料格式錯誤</div>';
    }
  } catch (error) {
    console.error('[AI Game] 載入活動失敗:', error);
    gameHtml.value = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; color: #f56c6c;">載入遊戲失敗：' + error.message + '</div>';
  }
};

// 從 localStorage 載入預覽資料
const loadPreviewData = () => {
  const previewData = localStorage.getItem('game_preview_data');

  if (previewData) {
    try {
      const data = JSON.parse(previewData);

      if (data.gameParameters && data.gameParameters.customCode) {
        gameConfig.value = {
          topic: data.gameParameters.topic || '自定義遊戲預覽',
          customCode: data.gameParameters.customCode
        };

        gameHtml.value = gameConfig.value.customCode;

        console.log('[AI Game] 預覽資料載入成功');
      }
    } catch (error) {
      console.error('[AI Game] 解析預覽資料失敗:', error);
    }
  }
};

// 初始化 Auth Token
const initializeAuth = async () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_token_expiry");
  try {
    const res = await api.main.getTokenUnlogin();
    const exp = jwt_decode(res.auth_token).exp;
    localStorage.setItem("auth_token", res.auth_token);
    localStorage.setItem("auth_token_expiry", exp);
    console.log('[AI Game] Auth token 初始化成功');
  } catch (error) {
    console.error("[AI Game] Auth token 初始化失敗:", error);
  }
};

// 初始化
onMounted(async () => {
  console.log('[AI Game] 頁面初始化');
  console.log('[AI Game] Route query:', route.query);

  // 判斷載入來源
  if (route.query.actId) {
    // 從 actId 載入 - 先初始化 auth_token
    await initializeAuth();
    loadActivityFromActId(route.query.actId);
  } else {
    // 從 localStorage 載入預覽資料
    loadPreviewData();
  }
});
</script>

<style lang="less" scoped>
@import "../assets/gameActManager.less";

.aigame-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #f5f5f5;
}

// 遊戲開始前
.before-game-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

// 正式活動模式
.actid-mode-wrapper {
  width: 90%;
  max-width: 900px;
}

.actid-mode-modal {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 30px 20px;
  }
}

.game-info-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.game-title {
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
}

.game-info {
  margin-bottom: 30px;
}

.game-type {
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
}

.left-buttons {
  width: 100%;
}

.play-button {
  width: 100%;
  padding: 16px;
  font-size: 18px;
  font-weight: 600;
  background: @game-primary-color;
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background: @game-hover-color;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }

  .play-icon {
    width: 24px;
    height: 24px;
  }
}

// 學生資訊輸入
.student-info-right {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.student-info-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.student-input {
  width: 100%;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: @game-primary-color;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
}

.error-text {
  display: block;
  margin-top: 8px;
  font-size: 14px;
  color: #f56c6c;
}

// 預覽模式
.preview-mode-wrapper {
  width: 90%;
  max-width: 500px;
}

.preview-modal {
  background: white;
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: #333;
    margin-bottom: 16px;
  }

  p {
    font-size: 18px;
    color: #666;
    margin-bottom: 30px;
  }

  .start-button {
    width: 100%;
    padding: 16px;
    font-size: 18px;
    font-weight: 600;
    background: @game-primary-color;
    color: white;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      background: @game-hover-color;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }
  }
}

// 遊戲區域
.game-iframe-container {
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.game-iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}
</style>
